/**
 * CancelSubscriptionSection Component
 *
 * Displays subscription cancellation status and provides cancel/reactivate actions.
 * - If active: Shows "Cancel Subscription" button with confirmation dialog
 * - If cancel_at_period_end: Shows warning banner + "Keep Subscription" button
 */

import { useState } from 'react';
import { Error as ErrorIcon, Warning } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  FormLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type { CancelSubscriptionRequest } from '@store/generated/subscriptions';

import { useCancelSubscriptionMutation, useReactivateSubscriptionMutation } from '../store/api';

import {
  type CancelReasonCategoryId,
  getShuffledCancelReasons,
  OTHER_CATEGORY_ID,
} from './cancelReasons';

interface CancelSubscriptionSectionProps {
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string | number | null;
  subscriptionStatus?: string | null;
  onStatusChange?: () => void;
}

// Format date (handles both ISO string and unix timestamp)
const formatDate = (dateValue: string | number | null | undefined): string => {
  if (!dateValue) return 'N/A';
  // If it's a number (Unix timestamp), convert from seconds to milliseconds
  const date = typeof dateValue === 'number' ? new Date(dateValue * 1000) : new Date(dateValue);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

export function CancelSubscriptionSection({
  cancelAtPeriodEnd,
  currentPeriodEnd,
  subscriptionStatus,
  onStatusChange,
}: CancelSubscriptionSectionProps) {
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<CancelReasonCategoryId[]>([]);
  const [cancellationReason, setCancellationReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Shuffle the non-Other reasons once per mount (Fisher–Yates) so the survey
  // order varies across sessions and no option benefits from primacy bias.
  // "Other" is always pinned last (GDN-3118).
  const [reasonCategories] = useState(getShuffledCancelReasons);

  const { subscriptionManagement } = useEventTracking();

  const [cancelSubscription, { isLoading: isCanceling }] = useCancelSubscriptionMutation();
  const [reactivateSubscription, { isLoading: isReactivating }] =
    useReactivateSubscriptionMutation();

  // Don't show section if no subscription, already canceled, or paused.
  // Paused subscriptions have no active billing period, so cancel_at_period_end
  // is not a valid Stripe operation — users should resume or let it lapse.
  if (!subscriptionStatus || subscriptionStatus === 'canceled' || subscriptionStatus === 'paused') {
    return null;
  }

  const handleCancelClick = () => {
    subscriptionManagement.cancelClicked();
    setShowCancelDialog(true);
    setError(null);
  };

  const handleCloseCancelDialog = () => {
    setShowCancelDialog(false);
    setSelectedCategories([]);
    setCancellationReason('');
    setError(null);
  };

  // Toggle a category while preserving selection order so the first-chosen
  // category is the primary reason the server maps onto Stripe's feedback enum.
  const handleToggleCategory = (categoryId: CancelReasonCategoryId) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId) ? prev.filter(id => id !== categoryId) : [...prev, categoryId]
    );
  };

  const hasSelection = selectedCategories.length > 0;
  const isOtherSelected = selectedCategories.includes(OTHER_CATEGORY_ID);
  const isFreeTextRequired = isOtherSelected;
  const isFreeTextMissing = isFreeTextRequired && !cancellationReason.trim();
  // Submit is blocked until a reason is chosen and, when "Other" is picked, the
  // free-text box is non-empty (trimmed) so we always learn what "Other" meant.
  const isSubmitBlocked = !hasSelection || isFreeTextMissing;

  const handleConfirmCancel = async () => {
    // Client-side gate: at least one category is required before submit.
    if (!hasSelection) {
      setError('Please select at least one reason for canceling.');
      return;
    }
    // When "Other" is selected, free text is required so the reason is captured.
    if (isFreeTextMissing) {
      setError('Please tell us a bit more since you selected "Other".');
      return;
    }
    setError(null);
    try {
      // `selectedCategories` uses the web-app's own reason-id union
      // (cancelReasons.ts). Cast it to the generated `categories` enum so a
      // codegen lag between the two repos can't break the web typecheck: the new
      // GDN-3118 behavioral-health ids land here before the deployed server
      // schema the FE regenerates against carries them. Deploy order is
      // web-first, and the server validates the actual values once it ships.
      const requestBody: CancelSubscriptionRequest = {
        categories: selectedCategories as CancelSubscriptionRequest['categories'],
        cancellation_reason: cancellationReason || null,
      };
      await cancelSubscription({
        cancelSubscriptionRequest: requestBody,
      }).unwrap();
      subscriptionManagement.cancelConfirmed({
        cancellation_reason_provided: !!cancellationReason.trim(),
        category_count: selectedCategories.length,
        primary_category: selectedCategories[0],
        categories: selectedCategories.join(','),
      });
      handleCloseCancelDialog();
      onStatusChange?.();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to cancel subscription. Please try again.';
      setError(errorMessage);
    }
  };

  const handleReactivate = async () => {
    setError(null);
    subscriptionManagement.reactivateClicked();
    try {
      await reactivateSubscription().unwrap();
      subscriptionManagement.reactivateConfirmed();
      onStatusChange?.();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to reactivate subscription. Please try again.';
      setError(errorMessage);
    }
  };

  // Show reactivation UI if subscription is set to cancel at period end
  if (cancelAtPeriodEnd) {
    return (
      <Box>
        <Alert
          severity="warning"
          icon={<Warning />}
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleReactivate}
              disabled={isReactivating}
              sx={{ fontWeight: 600 }}
            >
              {isReactivating ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                'Keep Subscription'
              )}
            </Button>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Your subscription is scheduled to cancel
          </Typography>
          <Typography variant="body2">
            You will lose access on {formatDate(currentPeriodEnd)}. Click &quot;Keep
            Subscription&quot; to continue your plan.
          </Typography>
        </Alert>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
    );
  }

  // Show cancel subscription UI for active subscriptions
  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'error.main',
        bgcolor: theme => `${theme.palette.error.main}08`,
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.main', mb: 1 }}>
        Danger Zone
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        If you cancel, you&apos;ll continue to have access until the end of your current billing
        period ({formatDate(currentPeriodEnd)}).
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="outlined"
        color="error"
        onClick={handleCancelClick}
        disabled={isCanceling}
        startIcon={isCanceling ? <CircularProgress size={16} /> : undefined}
      >
        Cancel Subscription
      </Button>

      {/* Confirmation Dialog */}
      <Dialog open={showCancelDialog} onClose={handleCloseCancelDialog} fullWidth maxWidth={"sm"}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorIcon color="error" />
          Cancel Subscription
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Are you sure you want to cancel your subscription? You will continue to have access
            until <strong>{formatDate(currentPeriodEnd)}</strong>, after which you will lose access
            to premium features.
          </DialogContentText>
          <FormLabel
            component="legend"
            required
            sx={{ display: 'block', mb: 1, fontWeight: 600, color: 'text.primary' }}
          >
            Why are you canceling? Select all that apply.
          </FormLabel>
          <FormGroup sx={{ mb: 3 }}>
            {reasonCategories.map(category => (
              <FormControlLabel
                key={category.id}
                control={
                  <Checkbox
                    checked={selectedCategories.includes(category.id)}
                    onChange={() => handleToggleCategory(category.id)}
                    disabled={isCanceling}
                  />
                }
                label={category.label}
              />
            ))}
          </FormGroup>
          <TextField
            label={isFreeTextRequired ? 'Please tell us more' : 'Anything else? (optional)'}
            required={isFreeTextRequired}
            error={isFreeTextMissing}
            helperText={
              isFreeTextMissing ? 'Required because you selected "Other".' : undefined
            }
            multiline
            rows={3}
            fullWidth
            value={cancellationReason}
            onChange={e => setCancellationReason(e.target.value)}
            placeholder="We'd love to know more so we can improve..."
            variant="outlined"
            disabled={isCanceling}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseCancelDialog} disabled={isCanceling}>
            Keep Subscription
          </Button>
          <Button
            onClick={handleConfirmCancel}
            color="error"
            variant="contained"
            disabled={isCanceling || isSubmitBlocked}
            startIcon={isCanceling ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isCanceling ? 'Canceling...' : 'Yes, Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
